import { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'

interface ApiDocsProps {
  specUrl: string
}

export default function ApiDocs({ specUrl }: ApiDocsProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Dynamically load Redoc
    const loadRedoc = async () => {
      if (!containerRef.current) return

      // Check if Redoc is available
      if (typeof (window as unknown as Record<string, unknown>).Redoc !== 'undefined') {
        const Redoc = (window as unknown as { Redoc: { init: (url: string, options: Record<string, unknown>, element: HTMLElement) => void } }).Redoc
        Redoc.init(specUrl, {
          theme: {
            colors: {
              primary: { main: '#0066CC' },
            },
            typography: {
              fontSize: '14px',
              fontFamily: '"Inter", sans-serif',
              headings: {
                fontFamily: '"Inter", sans-serif',
                fontWeight: '600',
              },
              code: {
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '13px',
              },
            },
            sidebar: {
              backgroundColor: '#F8FAFC',
              width: '260px',
            },
            rightPanel: {
              backgroundColor: '#1E293B',
            },
          },
          scrollYOffset: 0,
          hideDownloadButton: false,
          expandResponses: '200',
          pathInMiddlePanel: true,
          jsonSampleExpandLevel: 2,
          nativeScrollbars: true,
        }, containerRef.current)
      } else {
        // Load Redoc script if not available
        const script = document.createElement('script')
        script.src = 'https://cdn.redoc.ly/redoc/latest/bundles/redoc.standalone.js'
        script.onload = () => {
          if (containerRef.current) {
            const Redoc = (window as unknown as { Redoc: { init: (url: string, options: Record<string, unknown>, element: HTMLElement) => void } }).Redoc
            Redoc.init(specUrl, {
              theme: {
                colors: {
                  primary: { main: '#0066CC' },
                },
                typography: {
                  fontSize: '14px',
                  fontFamily: '"Inter", sans-serif',
                },
              },
              nativeScrollbars: true,
            }, containerRef.current)
          }
        }
        document.head.appendChild(script)
      }
    }

    loadRedoc()
  }, [specUrl])

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        overflow: 'auto',
      }}
    >
        {/* Loading state */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography color="text.secondary">Loading API documentation...</Typography>
        </Box>
    </Box>
  )
}
